import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useRunBuildGate } from '@/lib/permissions';
import { useRunBuild } from '@/shell/RunBuildContext';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { BuildsSearch } from '@/router';
import { BuildsView } from '@/features/builds/BuildsView';
import { useBuildsPageData } from '@/features/builds/useBuildsPageData';

/** Screen 02: filter bar + table (cards on phones), cursor pagination, live through the SSE `builds` topic. */
export function BuildsPage() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const search = useSearch({ strict: false }) as BuildsSearch;
  const navigate = useNavigate();
  const { openRunBuild } = useRunBuild();
  const data = useBuildsPageData(slug, search);
  const runBuildGate = useRunBuildGate(data.project?.id);
  const setSearch = (next: BuildsSearch) => void navigate({ to: '/projects/$slug/builds', params: { slug }, search: next, replace: true });

  const { projects, project } = data;
  if (projects.isLoading && !projects.data) return <PageSkeleton />;
  if (projects.isError && !projects.data) return <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />;
  if (!project) return <ProjectNotFound slug={slug} />;
  return <BuildsView project={project} slug={slug} search={search} setSearch={setSearch} data={data} runBuildGate={runBuildGate} onRunBuild={() => openRunBuild({ projectSlug: slug })} />;
}

function ProjectNotFound({ slug }: { slug: string }) {
  return (
    <EmptyState
      icon="account_tree"
      title="Project not found"
      description={`There is no project with slug “${slug}” in this organization.`}
      action={
        <Button asChild variant="outline">
          <Link to="/">Back to Overview</Link>
        </Button>
      }
    />
  );
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-4 w-80" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}
